import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatMskDateTime } from '../../matching/routes/Dashboard/core/mskTime'
import { matchingTicketCardUrl } from '../../../common/MatchingTicketLink'
import { BODY, BORDER, INK, MONO, MUTED, POINT_TONE_OF, RAW_BLOCK, SOFT } from '../core/consts'
import { TimelinePoint } from '../core/response.types'
import { CashBoxAttempts } from './CashBoxAttempts'
import { CashBoxAttempt } from '../core/cashBoxAttempts.types'
import { formatMskTime } from '../../matching/routes/Dashboard/core/mskTime'

/**
 * Ссылку строит интерфейс, а не бэкенд: маршруты админки — его знание, и один и тот же вид точки
 * из разных разделов может вести в разные места. Бэкенд отдаёт только вид точки и токен.
 */
const TimelinePointItem: React.FC<{
  point: TimelinePoint
  last: boolean
  ticketToken?: string
  /** A long-lived history spans days: show the MSK date with the time, in a wider column. */
  withDate?: boolean
}> = ({ point, last, ticketToken, withDate = false }) => {
  const [rawOpen, setRawOpen] = useState(false)
  const tone = POINT_TONE_OF(point)
  const cashBoxAttempts =
    point.kind === 'ADAPTER_ATTEMPT' && Array.isArray(point.fields?.cashBoxAttempts)
      ? (point.fields.cashBoxAttempts as CashBoxAttempt[])
      : null
  const matchingCardUrl =
    point.kind === 'MATCHING_REFUSED' && ticketToken ? matchingTicketCardUrl(ticketToken) : null
  // Точка истории выплаты про депозит несёт его токен; адрес — знание интерфейса.
  const depositCardUrl =
    typeof point.fields.depositToken === 'string'
      ? matchingTicketCardUrl(point.fields.depositToken)
      : null
  // Строки разбора, собранные бэкендом (правила каскада, части решения мэтчинга).
  const lines = Array.isArray(point.fields.lines)
    ? (point.fields.lines as unknown[]).filter((l): l is string => typeof l === 'string')
    : []

  return (
    <div className='d-flex' style={{ gap: 16 }}>
      <div style={{ width: withDate ? 150 : 70, flexShrink: 0, textAlign: 'right', paddingTop: 6 }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 12,
            color: SOFT,
            ...(withDate && { whiteSpace: 'nowrap' }),
          }}
        >
          {withDate ? formatMskDateTime(point.at) : formatMskTime(point.at)}
        </div>
      </div>

      <div className='d-flex flex-column align-items-center' style={{ width: 32, flexShrink: 0 }}>
        <div
          className='d-flex align-items-center justify-content-center'
          style={{ width: 32, height: 32, borderRadius: 8, background: tone.bg }}
        >
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: tone.dot }} />
        </div>
        {!last && <div style={{ flexGrow: 1, width: 2, background: BORDER }} />}
      </div>

      <div
        className='d-flex flex-column'
        style={{ flexGrow: 1, gap: 4, paddingTop: 5, paddingBottom: last ? 0 : 18, minWidth: 0 }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{point.title}</div>
        <div style={{ fontSize: 13, color: BODY }}>{point.summary}</div>

        {lines.length > 0 && (
          <ul style={{ margin: '2px 0 0', paddingLeft: 18, fontSize: 12.5, color: BODY }}>
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}

        {/* Решение мэтчинга одно на депозит, сколько бы кругов каскада ни было дальше: журнал
            решений уникален по тикету, и повторное решение в него уже не попадает. Без этой
            строки одно событие между двумя кругами читается как потерянное. */}
        {point.kind === 'MATCHING_REFUSED' && (
          <div style={{ fontSize: 12, color: MUTED }}>
            Мэтчинг записывает только первое решение по депозиту
          </div>
        )}

        {cashBoxAttempts && (
          <div style={{ marginTop: 4 }}>
            <CashBoxAttempts attempts={cashBoxAttempts} />
          </div>
        )}

        {(matchingCardUrl || point.raw) && (
          <div className='d-flex align-items-center' style={{ gap: 8, marginTop: 4 }}>
            {matchingCardUrl && (
              <Link to={matchingCardUrl} className='btn btn-sm btn-light-primary' style={{ fontSize: 12.5 }}>
                Карточка матчинг-тикета
              </Link>
            )}
            {/* Раскрытие существует только когда ключ пришёл: ниже ROLE_MANAGER бэкенд не отдаёт
                `raw` вовсе, поэтому раскрывать нечего и пустого блока не появляется. */}
            {point.raw && (
              <button
                type='button'
                className='btn btn-sm btn-light'
                style={{ fontSize: 12.5 }}
                onClick={() => setRawOpen((open) => !open)}
              >
                {rawOpen ? 'Скрыть сырые данные' : 'Показать сырые данные'}
              </button>
            )}
          </div>
        )}

        {rawOpen && point.raw && (
          <pre style={{ ...RAW_BLOCK, margin: '6px 0 0', maxHeight: 320 }}>
            {JSON.stringify(point.raw, null, 2)}
          </pre>
        )}

        {depositCardUrl && (
          <div className='d-flex align-items-center' style={{ gap: 8, marginTop: 4 }}>
            <Link
              to={depositCardUrl}
              className='btn btn-sm btn-light-primary'
              style={{ fontSize: 12.5 }}
            >
              Карточка депозита
            </Link>
          </div>
        )}

        {point.actor !== 'system' && <div style={{ fontSize: 12, color: MUTED }}>{point.actor}</div>}
      </div>
    </div>
  )
}

export default TimelinePointItem
